import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback } from "react";
import {
  Card,
  Stack,
  Container,
  Typography,
  Grid,
  LinearProgress,
  useTheme,
  Button,
} from "@mui/material";
import { get, post } from "src/utils/networkUtils";
import { useSearchParams } from "react-router-dom";

export default function TenantOnboardingPage() {
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [isFixing, setIsFixing] = useState(false);
  const tenantId = searchParams.get("tenantId");
  const theme = useTheme();

  const loadStats = useCallback(() => {
    get(`/admin/tenants/${tenantId}/onboarding-stats`).then(function (response) {
      setStats(response.stats);
    });
  }, [tenantId]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const getOnboardingStats = () => {
    if (!stats) return null;
    const uncompletedOnboardingPacksCount = stats.totalUsersCount - stats.withoutOnboardingPacksCount;

    const uncompletedOnboardingDivisionsCount = stats.totalUsersCount - stats.withoutOnboardingDivisionsCount;
    return {
      uncompletedOnboardingPacksCount,
      uncompletedOnboardingDivisionsCount,
    };
  }
  const getProgressValue = (completed, total) => {
    if (total === 0) return 0;
    return (completed / total) * 100;
  };


  const handleFixTenantOnboarding = () => {
    setIsFixing(true);
    post(`/admin/tenants/${tenantId}/fix-onboarding`).then(function (response) {
      // Reload the stats after fixing
      loadStats();
      setIsFixing(false);
    }).catch(function (error) {
      console.error('Error fixing tenant onboarding:', error);
      setIsFixing(false);
    });
  };
  return (
    <>
      <Helmet>
        <title> Tenant Onboarding </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Tenant Onboarding
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleFixTenantOnboarding}
            disabled={isFixing}
          >
            {isFixing ? 'Fixing...' : 'Fix Tenant Onboarding'}
          </Button>
        </Stack>

        <Card>
          <Grid container spacing={3} padding={4}>
            <Grid item xs={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">
                    Onboarding Packs: {getOnboardingStats()?.uncompletedOnboardingPacksCount}/{stats?.totalUsersCount} Completed
                  </Typography>
                </Grid>
                <Grid item xs={12} sx={{ position: 'relative' }}>
                  <LinearProgress
                    variant="determinate"
                    value={getProgressValue(
                      getOnboardingStats()?.uncompletedOnboardingPacksCount,
                      stats?.totalUsersCount
                    )}
                    sx={{
                      height: 10,
                      backgroundColor: theme.palette.action.hover,
                      borderRadius: 2,
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: theme.palette["primary"].main,
                        borderRadius: 2,
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: '8px',
                      transform: 'translateY(-50%)',
                      fontWeight: 'bold',
                      color: theme.palette.text.primary,
                      fontSize: '0.75rem',
                    }}
                  >
                    {getProgressValue(
                      getOnboardingStats()?.uncompletedOnboardingPacksCount,
                      stats?.totalUsersCount
                    ).toFixed(1)}%
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">
                    Divisions: {getOnboardingStats()?.uncompletedOnboardingDivisionsCount}/{stats?.totalUsersCount} Completed
                  </Typography>
                </Grid>
                <Grid item xs={12} sx={{ position: 'relative' }}>
                  <LinearProgress
                    variant="determinate"
                    value={getProgressValue(
                      getOnboardingStats()?.uncompletedOnboardingDivisionsCount,
                      stats?.totalUsersCount
                    )}
                    sx={{
                      height: 10,
                      backgroundColor: theme.palette.action.hover,
                      borderRadius: 2,
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: theme.palette["primary"].main,
                        borderRadius: 2,
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: '8px',
                      transform: 'translateY(-50%)',
                      fontWeight: 'bold',
                      color: theme.palette.text.primary,
                      fontSize: '0.75rem',
                    }}
                  >
                    {getProgressValue(
                      getOnboardingStats()?.uncompletedOnboardingDivisionsCount,
                      stats?.totalUsersCount
                    ).toFixed(1)}%
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Card>
      </Container>
    </>
  );
}
